// Client Addresses tab. List + inline "+ Address" form. Ported from
// the original ClientDetailPage Addresses <TabsContent> block (PR #98,
// renamed/extended PR #101 — Address Type + Company Name + Use For
// dropdown).
import { DeleteEntityAction } from "@/components/common/DeleteEntityAction";
import { ClientShippingAccounts } from "@/components/clients/ClientShippingAccounts";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useOutletContext } from "react-router-dom";
import { MapPin } from "lucide-react";
import { ApiError, api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { StatusPill } from "@/components/ui/StatusPill";
import type { ClientDetailContext } from "@/pages/ClientDetailPage";

interface AddressRow {
  id: string;
  label: string;
  companyName: string | null;
  attention: string | null;
  street1: string;
  street2: string | null;
  city: string;
  stateCode: string;
  postalCode: string;
  countryCode: string | null;
  isDefaultShipTo: boolean | null;
  isDefaultBillTo: boolean | null;
}

type AddressUseFor = "billing" | "shipping" | "both" | "neither";

export function ClientAddressesTab() {
  const { canEdit, clientId } = useOutletContext<ClientDetailContext>();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["clients", clientId, "addresses"],
    queryFn: () =>
      api<{ addresses: AddressRow[] }>(`/clients/${clientId}/addresses`),
    enabled: clientId.length > 0,
  });

  const addresses = data?.addresses ?? [];

  const [isCreating, setIsCreating] = useState(false);
  // Editing an existing address (Stephanie 2026-10-14: "why cant i edit this
  // address") — same form, prefilled, saved with PATCH /addresses/:id.
  const [editing, setEditing] = useState<AddressRow | null>(null);
  const [label, setLabel] = useState("");
  const [company, setCompany] = useState("");
  const [street1, setStreet1] = useState("");
  const [street2, setStreet2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postal, setPostal] = useState("");
  const [country, setCountry] = useState("US");
  const [useFor, setUseFor] = useState<AddressUseFor>("both");
  const [error, setError] = useState<string | null>(null);

  const startEdit = (a: AddressRow): void => {
    setIsCreating(false);
    setEditing(a);
    setLabel(a.label);
    setCompany(a.companyName ?? "");
    setStreet1(a.street1);
    setStreet2(a.street2 ?? "");
    setCity(a.city);
    setState(a.stateCode);
    setPostal(a.postalCode);
    setCountry(a.countryCode ?? "US");
    setUseFor(
      a.isDefaultShipTo && a.isDefaultBillTo
        ? "both"
        : a.isDefaultShipTo
          ? "shipping"
          : a.isDefaultBillTo
            ? "billing"
            : "neither",
    );
    setError(null);
  };

  const resetForm = (): void => {
    setEditing(null);
    setLabel("");
    setCompany("");
    setStreet1("");
    setStreet2("");
    setCity("");
    setState("");
    setPostal("");
    setCountry("US");
    setUseFor("both");
    setError(null);
  };

  // Route schema (apps/api/src/routes/addresses.ts) uses snake_case with
  // underscores between "street" and the number — `street_1` / `street_2`,
  // NOT `street1` / `street2`. Field-name mismatch caused a 400 in the
  // first cut of this form (fix: PR #99).
  const createMutation = useMutation({
    mutationFn: async (body: {
      label: string;
      company_name?: string;
      attention?: string;
      street_1: string;
      street_2?: string;
      city: string;
      state_code: string;
      postal_code: string;
      country_code?: string;
      is_default_ship_to?: boolean;
      is_default_bill_to?: boolean;
    }) =>
      editing
        ? api<{ address: { id: string } }>(`/addresses/${editing.id}`, {
            method: "PATCH",
            // Editing: a cleared optional field is cleared (null).
            body: JSON.stringify({
              ...body,
              company_name: body.company_name ?? null,
              street_2: body.street_2 ?? null,
            }),
          })
        : api<{ address: { id: string } }>(`/clients/${clientId}/addresses`, {
            method: "POST",
            body: JSON.stringify(body),
          }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["clients", clientId, "addresses"],
      });
      setIsCreating(false);
      resetForm();
    },
    onError: (err) => {
      const verb = editing ? "save" : "create";
      setError(
        err instanceof ApiError
          ? `Couldn't ${verb} address (${err.status})`
          : `Couldn't ${verb} address — see console`,
      );
    },
  });

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">
            Addresses{" "}
            <span className="font-normal text-muted">({addresses.length})</span>
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            Changing a default here doesn't change orders already placed — each order keeps the address it was sent with.
          </p>
        </div>
        {canEdit && !isCreating && !editing && (
          <Button
            variant="outline"
            onClick={() => {
              resetForm();
              setIsCreating(true);
            }}
          >
            Add address
          </Button>
        )}
      </div>

      {(isCreating || editing) && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (
              label.trim().length === 0 ||
              street1.trim().length === 0 ||
              city.trim().length === 0 ||
              state.trim().length === 0 ||
              postal.trim().length === 0
            ) {
              setError(
                "Label, street, city, state, and postal code are required.",
              );
              return;
            }
            setError(null);
            const wantsShip = useFor === "shipping" || useFor === "both";
            const wantsBill = useFor === "billing" || useFor === "both";
            createMutation.mutate({
              label: label.trim(),
              company_name:
                company.trim().length > 0 ? company.trim() : undefined,
              street_1: street1.trim(),
              street_2:
                street2.trim().length > 0 ? street2.trim() : undefined,
              city: city.trim(),
              state_code: state.trim().toUpperCase(),
              postal_code: postal.trim(),
              country_code: country.trim().toUpperCase() || "US",
              is_default_ship_to: wantsShip,
              is_default_bill_to: wantsBill,
            });
          }}
          className="mb-4 rounded-md border border-rule bg-white p-5 sm:p-[22px]"
        >
          <h3 className="mb-3 text-lg font-semibold tracking-[-0.4px] text-ink">{editing ? "Edit address" : "New address"}</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block sm:col-span-2">
              <span className="mb-1 block text-xs font-medium text-muted">
                Address Type
              </span>
              <Input
                autoFocus
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                maxLength={120}
                placeholder="HQ / Warehouse / The Palatine — Move-In Office"
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="mb-1 block text-xs font-medium text-muted">
                Company Name{" "}
                <span className="font-normal text-brown-300">(optional)</span>
              </span>
              <Input
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                maxLength={200}
                placeholder="Acme Holdings"
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="mb-1 block text-xs font-medium text-muted">
                Street
              </span>
              <Input
                value={street1}
                onChange={(e) => setStreet1(e.target.value)}
                maxLength={200}
                placeholder="123 Main St"
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="mb-1 block text-xs font-medium text-muted">
                Street 2{" "}
                <span className="font-normal text-brown-300">(optional)</span>
              </span>
              <Input
                value={street2}
                onChange={(e) => setStreet2(e.target.value)}
                maxLength={200}
                placeholder="Suite 200"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-muted">
                City
              </span>
              <Input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                maxLength={120}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-muted">
                State
              </span>
              <Input
                value={state}
                onChange={(e) => setState(e.target.value)}
                maxLength={5}
                placeholder="FL"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-muted">
                Postal code
              </span>
              <Input
                value={postal}
                onChange={(e) => setPostal(e.target.value)}
                maxLength={20}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-muted">
                Country
              </span>
              <Input
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                maxLength={5}
                placeholder="US"
              />
            </label>
          </div>
          <label className="mt-3 block">
            <span className="mb-1 block text-xs font-medium text-muted">
              Use for
            </span>
            <select
              value={useFor}
              onChange={(e) => setUseFor(e.target.value as AddressUseFor)}
              className="w-full rounded-md border border-brown-100 bg-white px-3 py-2 text-sm text-brown-500 focus:border-ink focus:outline-none focus:ring-2 focus:ring-rust/30"
            >
              <option value="both">Both — shipping &amp; billing</option>
              <option value="shipping">Shipping only</option>
              <option value="billing">Billing only</option>
              {editing && <option value="neither">Neither — not a default</option>}
            </select>
            <span className="mt-1 block text-xs text-brown-300">
              Sets this address as the default for the selected role(s).
              Adding a second address with the same role replaces the prior
              default.
            </span>
          </label>
          {error && (
            <div className="mt-3 rounded-md border border-rust-200 bg-rust-50 px-3 py-2 text-sm text-rust-500">
              {error}
            </div>
          )}
          <div className="mt-4 flex gap-2">
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? "Saving…" : "Save address"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setIsCreating(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            {/* Delete an address (Stephanie 2026-10-30: anything a rep enters
                can be edited AND deleted). Soft delete — past orders keep the
                address they shipped to. */}
            {editing && (
              <div className="ml-auto">
                <DeleteEntityAction
                  deletePath={`/addresses/${editing.id}`}
                  triggerLabel="Delete address"
                  allowedRoles={["admin", "client_rep"]}
                  confirmTitle="Delete this address?"
                  confirmBody={
                    <>
                      This removes <strong className="font-semibold text-brown-500">{editing.label}</strong> from this
                      account&apos;s addresses. Past orders, POs and invoices keep the address they used.
                    </>
                  }
                  redirectTo={`/clients/${clientId}/addresses`}
                  flashMessage="Address deleted."
                  errorFallback="We couldn't delete this address. Nothing was changed — try again."
                  invalidateKeys={[["clients", clientId, "addresses"], ["clients", clientId, "detail"]]}
                />
              </div>
            )}
          </div>
        </form>
      )}

      {isLoading ? (
        <div className="h-24 animate-pulse rounded-md bg-white" aria-label="Loading addresses" />
      ) : addresses.length === 0 ? (
        !isCreating && (
          <div className="rounded-md border border-rule bg-white px-5 py-12 text-center">
            <p className="font-medium text-ink">No addresses yet.</p>
            <p className="mt-1 text-sm text-muted">Add where this account is billed and where orders ship.</p>
          </div>
        )
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2">
          {addresses.map((a) => (
            <li
              key={a.id}
              className="rounded-md border border-rule bg-white p-5"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="inline-flex items-center gap-2 font-medium text-ink">
                  <MapPin className="h-4 w-4 text-muted" aria-hidden />
                  {a.label}
                </span>
                <span className="flex flex-wrap items-center gap-1.5">
                  {a.isDefaultBillTo && <StatusPill tone="info">Default bill-to</StatusPill>}
                  {a.isDefaultShipTo && <StatusPill tone="active">Default ship-to</StatusPill>}
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => startEdit(a)}
                      className="ml-1 text-sm font-semibold text-ink hover:underline"
                      aria-label={`Edit ${a.label}`}
                    >
                      Edit
                    </button>
                  )}
                </span>
              </div>
              <div className="mt-2 text-sm leading-relaxed text-muted">
                {a.companyName && (
                  <>
                    <span className="font-medium text-ink">
                      {a.companyName}
                    </span>
                    <br />
                  </>
                )}
                {a.attention && (
                  <>
                    <span className="italic">c/o {a.attention}</span>
                    <br />
                  </>
                )}
                {a.street1}
                {a.street2 && (
                  <>
                    <br />
                    {a.street2}
                  </>
                )}
                <br />
                {a.city}, {a.stateCode} {a.postalCode}
              </div>
            </li>
          ))}
        </ul>
      )}
      <ClientShippingAccounts clientId={clientId} canEdit={canEdit} />
    </section>
  );
}
